import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  EmperorDialogRefService,
  EmperorDialogSize,
} from '@boldpenguin/emperor-presentational';
import { AppointmentTypes } from '@boldpenguin/sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
} from 'rxjs/operators';
import { EnrollmentsService } from 'src/app/core/services/enrollments-service/enrollments.service';
import {
  MixpanelEventType,
  MixpanelService,
} from 'src/app/core/services/mixpanel-service/mixpanel.service';
import { Messages } from 'src/app/core/services/notification-service/notification-messages';
import { NotificationService } from 'src/app/core/services/notification-service/notification.service';
import * as QuoteActions from 'src/app/features/quotes/store/quotes.actions';
import { AppointmentDialogComponent } from 'src/app/features/settings/components/appointment-dialog/appointment-dialog.component';
import {
  AppointmentAction,
  dialogConfigs,
} from 'src/app/shared/models/appointment-dialog.model';
import {
  getIncompleteCarrierQuestionGroups,
  isMainComplianceFormComplete,
} from 'src/app/shared/utilities/compliance-form-utilities';
import { IEnrollment } from '../../models/enrollments.model';
import { EnrollmentUpdateService } from '../../services/enrollment-update-service/enrollment-update.service';
import { getWelcomeDirectAppointmentActivateErrorMessage } from '../../services/notification-service/dynamic-notification-messages';
import {
  selectComplianceForm,
  selectComplianceFormHasBeenLoaded,
} from '../compliance-form/compliance-form.selectors';
import * as EnrollmentsActions from './enrollments.actions';
import {
  CoreEnrollmentActions,
  EnrollmentDialogActions,
} from './enrollments.actions';

@Injectable()
export class EnrollmentsEffects {
  getEnrollments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnrollmentsActions.GetAllEnrollments),
      switchMap(() =>
        this.enrollmentsService.getEnrollments().pipe(
          map((enrollments) =>
            EnrollmentsActions.GetAllEnrollmentsSuccess({ enrollments }),
          ),
          catchError((e: { error: Error }) =>
            of(EnrollmentsActions.GetAllEnrollmentsError(e)),
          ),
        ),
      ),
    ),
  );

  getOrganizationTenants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnrollmentsActions.GetAllEnrollmentsSuccess),
      map(() => QuoteActions.GetOrganizationTenants()),
    ),
  );

  updateEnrollmentCredentials$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnrollmentsActions.QuoteViewUpdateEnrollmentCredentials),
      switchMap((action) =>
        this.enrollmentUpdateService
          .updateEnrollment({
            enrollment: action.enrollment,
            activate: true,
            successMessage: dialogConfigs.editDirect.successMessage,
          })
          .pipe(
            mergeMap((enrollment) => [
              EnrollmentsActions.QuoteViewUpdateEnrollmentCredentialsSuccess({
                enrollment,
              }),
              QuoteActions.ResubmitCarrierQuote({
                carrierId: enrollment.carrier_id,
              }),
            ]),
            catchError((err: HttpErrorResponse) => {
              const errorMessages = this.mapEnrollmentUpdateErrors(err);

              return of(
                EnrollmentsActions.QuoteViewUpdateEnrollmentCredentialsError({
                  error: errorMessages,
                  enrollment: action.enrollment,
                }),
              );
            }),
          ),
      ),
    ),
  );

  startSubAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreEnrollmentActions.startSubAppointment),
      map(({ enrollment }) =>
        EnrollmentDialogActions.openDialog({
          data: {
            type: AppointmentAction.Connect,
            enrollment,
            enrollmentType: AppointmentTypes.Sub,
          },
        }),
      ),
    ),
  );

  startDirectAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CoreEnrollmentActions.startDirectAppointment,
        CoreEnrollmentActions.editDirectAppointment,
      ),
      map(({ enrollment }) => {
        const displayEdit =
          enrollment.active &&
          enrollment.appointment_type !== AppointmentTypes.PlacementDesk;

        return EnrollmentDialogActions.openDialog({
          data: {
            type: displayEdit
              ? AppointmentAction.Edit
              : AppointmentAction.Connect,
            enrollment,
            enrollmentType: AppointmentTypes.Direct,
          },
        });
      }),
    ),
  );

  activatePlacementDeskAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreEnrollmentActions.activatePlacementDeskAppointment),
      mergeMap(({ enrollment }) =>
        this.enrollmentsService
          .activatePlacementDeskEnrollment(enrollment)
          .pipe(
            map(() =>
              CoreEnrollmentActions.activatePlacementDeskAppointmentSuccess({
                enrollment: {
                  ...enrollment,
                  active: true,
                },
                successMessage:
                  'placement desk appointment has been activated.',
              }),
            ),
            catchError((error) =>
              of(
                CoreEnrollmentActions.activatePlacementDeskAppointmentError({
                  enrollment,
                  error: this.mapEnrollmentUpdateErrors(error),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  activePlacementDeskAppointmentSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreEnrollmentActions.activatePlacementDeskAppointmentSuccess),
        tap(({ enrollment, successMessage }) => {
          if (successMessage) {
            this.enrollmentUpdateService.displayUpdateSuccessMessage(
              enrollment.carrier_name,
              successMessage,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  viewAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreEnrollmentActions.viewAppointment),
      map(({ enrollment }) =>
        EnrollmentDialogActions.openDialog({
          data: {
            type: enrollment.appointment_pending
              ? AppointmentAction.Pending
              : AppointmentAction.View,
            enrollment,
            enrollmentType: enrollment.appointment_type,
          },
        }),
      ),
    ),
  );

  startDisconnect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreEnrollmentActions.startDisconnect),
      map(({ enrollment }) =>
        EnrollmentDialogActions.openDialog({
          data: {
            type: AppointmentAction.Disconnect,
            enrollment,
            enrollmentType: enrollment.appointment_type,
          },
        }),
      ),
    ),
  );

  dialogOpen$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EnrollmentDialogActions.openDialog),
        concatLatestFrom(() => this.checkComplianceAnswerWithPermission$()),
        tap(([{ data }, { incompleteQuestionGroups }]) => {
          this.mixpanelService.track(
            MixpanelEventType.APPOINTMENT_DIALOG_OPENED,
            {
              type: data.type,
              appointmentType: data.enrollmentType,
            },
          );
          this.emperorDialogRefService.open(AppointmentDialogComponent, {
            data: {
              ...data,
              incompleteQuestionGroups,
            },
            backdropDismiss: false,
            uiDialogSize: incompleteQuestionGroups.includes(
              data.enrollment.carrier_subdomain,
            )
              ? undefined
              : EmperorDialogSize.Medium,
          });
        }),
      ),
    { dispatch: false },
  );

  dialogRequestSubAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnrollmentDialogActions.requestSubAppointment),
      map(({ enrollment, successMessage }) => {
        if (enrollment.allow_instant_enrollment) {
          return CoreEnrollmentActions.activateAppointment({
            enrollment: {
              ...enrollment,
              appointment_type: AppointmentTypes.Sub,
            },
            successMessage,
          });
        } else {
          return CoreEnrollmentActions.requestSubAppointment({
            enrollment,
            successMessage,
          });
        }
      }),
    ),
  );

  dialogRequestDirectAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnrollmentDialogActions.editDirectAppointment),
      map(({ enrollment, successMessage }) =>
        CoreEnrollmentActions.activateAppointment({
          enrollment: {
            ...enrollment,
            appointment_type: AppointmentTypes.Direct,
          },
          successMessage,
        }),
      ),
    ),
  );

  dialogPreconfigureAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnrollmentDialogActions.preconfigureAppointment),
      map(({ enrollment, successMessage }) =>
        CoreEnrollmentActions.preconfigureAppointment({
          enrollment,
          successMessage,
        }),
      ),
    ),
  );

  dialogDeactivateAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnrollmentDialogActions.deactivateAppointment),
      tap(({ enrollment }) => {
        this.mixpanelService.track(
          MixpanelEventType.APPOINTMENT_DIALOG_OPENED,
          {
            type: AppointmentAction.Disconnect,
            appointmentType: enrollment.appointment_type,
          },
        );
      }),
      map(({ enrollment, successMessage }) =>
        CoreEnrollmentActions.deactivateAppointment({
          enrollment,
          successMessage,
        }),
      ),
    ),
  );

  activateAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreEnrollmentActions.activateAppointment),
      mergeMap(({ enrollment, successMessage }) =>
        this.enrollmentUpdateService
          .updateEnrollment({
            enrollment,
            activate: true,
            forceValidation: true,
            successMessage,
          })
          .pipe(
            map((updatedEnrollment) =>
              CoreEnrollmentActions.activateAppointmentSuccess({
                enrollment: updatedEnrollment,
                successMessage,
              }),
            ),
            catchError((error) =>
              of(
                CoreEnrollmentActions.activateAppointmentError({
                  enrollment,
                  error: this.mapEnrollmentUpdateErrors(error),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  preconfigureAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreEnrollmentActions.preconfigureAppointment),
      mergeMap(({ enrollment, successMessage }) =>
        this.enrollmentUpdateService
          .updateEnrollment({
            enrollment,
            activate: false,
            forceValidation: true,
            successMessage,
          })
          .pipe(
            map((updatedEnrollment) =>
              CoreEnrollmentActions.preconfigureAppointmentSuccess({
                enrollment: updatedEnrollment,
                successMessage,
              }),
            ),
            catchError((error) =>
              of(
                CoreEnrollmentActions.preconfigureAppointmentError({
                  enrollment,
                  error: this.mapEnrollmentUpdateErrors(error),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  requestSubAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreEnrollmentActions.requestSubAppointment),
      mergeMap(({ enrollment, successMessage }) =>
        this.enrollmentsService
          .requestEnrollmentAppointment(enrollment.id)
          .pipe(
            tap(() => {
              this.mixpanelService.track(
                MixpanelEventType.APPOINTMENT_REQUEST,
                {
                  carrier: enrollment.carrier_subdomain,
                  active: enrollment.active,
                  appointment_type: enrollment.appointment_type || '',
                },
              );
            }),
            map(() =>
              CoreEnrollmentActions.requestSubAppointmentSuccess({
                enrollment: {
                  ...enrollment,
                  appointment_pending: true,
                },
                successMessage,
              }),
            ),
            catchError((error) =>
              of(
                CoreEnrollmentActions.requestSubAppointmentError({
                  enrollment,
                  error: this.mapEnrollmentUpdateErrors(error),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  requestAppointmentSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreEnrollmentActions.requestSubAppointmentSuccess),
        tap(({ enrollment }) => {
          this.mixpanelService.track(
            MixpanelEventType.APPOINTMENT_REQUEST_SUCCESS,
            {
              carrier: enrollment.carrier_subdomain,
              active: enrollment.active,
              appointment_type: enrollment.appointment_type || '',
            },
          );

          this.notificationService.success(
            Messages.ENROLLMENT_APPOINTMENT_SUCCESS,
            { horizontalPosition: 'center' },
          );
        }),
      ),
    { dispatch: false },
  );

  requestAppointmentError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreEnrollmentActions.requestSubAppointmentError),
        tap(({ enrollment, error }) => {
          this.mixpanelService.track(
            MixpanelEventType.APPOINTMENT_REQUEST_ERROR,
            {
              carrier: enrollment.carrier_subdomain,
              active: enrollment.active,
              appointment_type: enrollment.appointment_type || '',
              error,
            },
          );

          this.notificationService.error(
            Messages.ENROLLMENT_APPOINTMENT_FAILURE,
            { horizontalPosition: 'center' },
          );
        }),
      ),
    { dispatch: false },
  );

  closeDialogs$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EnrollmentDialogActions.closeDialog),
        tap(() => this.dialogService.close()),
      ),
    { dispatch: false },
  );

  deactivateAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreEnrollmentActions.deactivateAppointment),
      mergeMap(({ enrollment, successMessage }) =>
        this.enrollmentUpdateService
          .updateEnrollment({
            enrollment,
            activate: false,
            forceValidation: false,
            successMessage,
          })
          .pipe(
            map((deactivatedEnrollment) =>
              CoreEnrollmentActions.deactivateAppointmentSuccess({
                enrollment: deactivatedEnrollment,
                successMessage,
              }),
            ),
            catchError((error) =>
              of(
                CoreEnrollmentActions.deactivateAppointmentError({
                  enrollment,
                  error: this.mapEnrollmentUpdateErrors(error),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  activateDirectEnrollments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnrollmentsActions.ActivateDirectAppointments),
      tap(({ enrollments }) => {
        this.mixpanelService.track(
          MixpanelEventType.WELCOME_DIRECT_APPOINTMENTS_ACTIVATE,
          {
            carriers: enrollments.map(
              (enrollment) => enrollment.carrier_subdomain,
            ),
          },
        );
      }),
      switchMap(({ enrollments }) => {
        if (!enrollments.length) {
          return of(
            EnrollmentsActions.ActivateDirectAppointmentsSuccess({
              enrollments: [],
            }),
          );
        }

        return combineLatest(
          enrollments.map((enrollment) =>
            this.enrollmentsService
              .activateDirectEnrollment(enrollment)
              .pipe(
                catchError((error) =>
                  throwError({ error, failedEnrollment: enrollment }),
                ),
              ),
          ),
        ).pipe(
          map((response: IEnrollment[]) =>
            EnrollmentsActions.ActivateDirectAppointmentsSuccess({
              enrollments: response,
            }),
          ),
          catchError(({ error: httpError, failedEnrollment }) =>
            of(
              EnrollmentsActions.ActivateDirectAppointmentsError({
                error: httpError.error,
                enrollments,
                failedEnrollment,
              }),
            ),
          ),
        );
      }),
    ),
  );

  activateDirectEnrollmentsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EnrollmentsActions.ActivateDirectAppointmentsSuccess),
        tap(({ enrollments }) => {
          this.mixpanelService.track(
            MixpanelEventType.WELCOME_DIRECT_APPOINTMENTS_ACTIVATE_SUCCESS,
            {
              carriers: enrollments.map(
                (enrollment) => enrollment.carrier_subdomain,
              ),
            },
          );
        }),
      ),
    { dispatch: false },
  );

  activateDirectEnrollmentsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EnrollmentsActions.ActivateDirectAppointmentsError),
        tap(({ enrollments, error, failedEnrollment }) => {
          this.notificationService.error(
            getWelcomeDirectAppointmentActivateErrorMessage(
              failedEnrollment.carrier_name,
            ),
            {
              duration: 9000,
            },
          );
          this.mixpanelService.track(
            MixpanelEventType.WELCOME_DIRECT_APPOINTMENTS_ACTIVATE_ERROR,
            {
              carriers: enrollments.map(
                (enrollment) => enrollment.carrier_subdomain,
              ),
              error: error.message,
            },
          );
        }),
        mergeMap(({ enrollments, failedEnrollment }) =>
          combineLatest(
            enrollments
              .filter((enrollment) => enrollment.id !== failedEnrollment.id)
              .map((enrollment) =>
                this.enrollmentUpdateService.updateEnrollment({
                  enrollment,
                  activate: false,
                }),
              ),
          ),
        ),
      ),
    { dispatch: false },
  );

  requestEnrollmentAppointments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnrollmentsActions.RequestEnrollmentAppointments),
      tap(({ enrollments }) => {
        this.mixpanelService.track(
          MixpanelEventType.WELCOME_SUBAPPOINTMENTS_REQUEST,
          {
            enrollments: enrollments.map(
              (enrollment) => enrollment.carrier_name,
            ),
          },
        );
      }),
      switchMap(({ enrollments, showSuccessMessage }) => {
        if (!enrollments.length) {
          return of(
            EnrollmentsActions.RequestEnrollmentAppointmentsSuccess({
              enrollments: [],
            }),
          );
        }

        const instantApprovalEnrollments = enrollments.filter(
          (enrollment) => enrollment.allow_instant_enrollment,
        );
        const manualApprovalEnrollments = enrollments.filter(
          (enrollment) => !enrollment.allow_instant_enrollment,
        );

        return combineLatest([
          ...manualApprovalEnrollments.map((enrollment) =>
            this.enrollmentsService.requestEnrollmentAppointment(enrollment.id),
          ),
          ...instantApprovalEnrollments.map((enrollment) =>
            this.enrollmentsService.updateEnrollment(
              {
                ...enrollment,
                appointment_type: AppointmentTypes.Sub,
              },
              true,
            ),
          ),
        ]).pipe(
          map(() =>
            EnrollmentsActions.RequestEnrollmentAppointmentsSuccess({
              enrollments,
              showSuccessMessage,
            }),
          ),
          catchError((error: Error) =>
            of(
              EnrollmentsActions.RequestEnrollmentAppointmentsError({
                error,
              }),
            ),
          ),
        );
      }),
    ),
  );

  requestEnrollmentAppointmentsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EnrollmentsActions.RequestEnrollmentAppointmentsSuccess),
        tap(({ enrollments, showSuccessMessage }) => {
          this.mixpanelService.track(
            MixpanelEventType.WELCOME_SUBAPPOINTMENTS_REQUEST_SUCCESS,
            {
              enrollments: enrollments.map(
                (enrollment) => enrollment.carrier_name,
              ),
            },
          );
          if (showSuccessMessage) {
            enrollments.forEach((enrollment) => {
              this.enrollmentUpdateService.displayUpdateSuccessMessage(
                enrollment.carrier_name,
                dialogConfigs.connectSub.successMessage ?? '',
              );
            });
          }
        }),
      ),
    { dispatch: false },
  );

  requestEnrollmentAppointmentsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EnrollmentsActions.RequestEnrollmentAppointmentsError),
        tap(({ error }) => {
          this.notificationService.error(Messages.UPDATE_FAILED);
          this.mixpanelService.track(
            MixpanelEventType.WELCOME_SUBAPPOINTMENTS_REQUEST_ERROR,
            {
              error: error.message,
            },
          );
        }),
      ),
    { dispatch: false },
  );

  getEnrollmentData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnrollmentsActions.CarrierAppointmentsLoaded),
      map(() => EnrollmentsActions.GetAllEnrollments()),
    ),
  );

  constructor(
    private actions$: Actions,
    private emperorDialogRefService: EmperorDialogRefService,
    private enrollmentsService: EnrollmentsService,
    private enrollmentUpdateService: EnrollmentUpdateService,
    private mixpanelService: MixpanelService,
    private notificationService: NotificationService,
    private store: Store,
    private dialogService: EmperorDialogRefService,
  ) {}

  private mapEnrollmentUpdateErrors(
    errorResponse: HttpErrorResponse,
  ): string[] {
    const errorMessages: string[] = [];
    if (errorResponse.status === 422) {
      for (const field in errorResponse.error.errors) {
        if (errorResponse.error.errors.hasOwnProperty(field)) {
          errorMessages.push(...errorResponse.error.errors[field]);
        }
      }
    }

    return errorMessages;
  }

  private checkComplianceAnswerWithPermission$(): Observable<{
    isComplianceFormComplete: boolean;
    incompleteQuestionGroups: string[];
  }> {
    return this.store.pipe(
      select(selectComplianceFormHasBeenLoaded),
      filter(Boolean),
      switchMap(() =>
        this.store.pipe(
          select(selectComplianceForm),
          // eslint-disable-next-line @ngrx/avoid-mapping-selectors
          map((complianceResponse) => ({
            isComplianceFormComplete: isMainComplianceFormComplete(
              complianceResponse?.completed_question_groups || [],
            ),
            incompleteQuestionGroups: getIncompleteCarrierQuestionGroups(
              complianceResponse?.completed_question_groups || [],
            ),
          })),
        ),
      ),
    );
  }
}
